import { Property } from '../types';

/**
 * Bilingual Cameroon Real Estate Lead Follow-Up Templates
 * Tailored for Notaries, Licensed Surveyors (OGEC), Agents, and Sellers
 * In accordance with Cameroon Land Ordinance 74-1 and MINDCAF Cadastral procedures.
 */

export interface LeadTemplate {
  id: 'visit_confirm' | 'title_dossier_req' | 'offer_escrow_referral' | 'counter_offer' | 'availability_brief';
  title: string;
  category: 'visit' | 'legal' | 'negotiation' | 'general';
  description: string;
  subject: string;
  template: string;
}

export interface LeadPlaceholderData {
  buyerName?: string;
  propertyTitle?: string;
  propertyLocation?: string;
  propertyPrice?: string;
  ownerName?: string;
  ownerPhone?: string;
  visitDate?: string;
  visitTime?: string;
  meetingLandmark?: string;
  notaryName?: string;
  escrowPercentage?: string;
  counterPrice?: string;
}

export function getFollowUpTemplates(language: 'fr' | 'en'): LeadTemplate[] {
  const isFr = language === 'fr';

  if (isFr) {
    return [
      {
        id: 'visit_confirm',
        title: 'Confirmation de Visite de Terrain',
        category: 'visit',
        description: 'Rendez-vous sur le site, repère géographique et contact du guide.',
        subject: 'Confirmation de visite : {propertyTitle} à {propertyLocation}',
        template: `Bonjour {buyerName},

Nous confirmons votre visite programmée pour le bien « {propertyTitle} » situé à {propertyLocation} le {visitDate} à {visitTime}.

📍 Point de ralliement : {meetingLandmark}
👤 Votre guide sur place : {ownerName} (Tél : {ownerPhone})

💡 Conseil pratique : Pour les terrains bruts, prévoyez des chaussures adaptées afin de parcourir aisément le bornage contradictoire et inspecter les bornes cadastrales OGEC.

Restons en contact dès votre approche du point de rencontre.
Bien cordialement,
{ownerName} — Cameroon Land Market`,
      },
      {
        id: 'title_dossier_req',
        title: 'Transmission Dossier Titre Foncier (MINDCAF)',
        category: 'legal',
        description: 'Envoi du certificat de propriété, plan de bornage et pièces du Livre Foncier.',
        subject: 'Dossier Titre Foncier & Cadastre : {propertyTitle}',
        template: `Bonjour {buyerName},

Faisant suite à votre demande sur Cameroon Land Market concernant « {propertyTitle} » ({propertyLocation}), nous préparons votre dossier de vérification cadastrale (Ordonnance 74-1).

Le dossier juridique comprend :
1. Copie conforme du Titre Foncier (Livre Foncier MINDCAF)
2. Certificat de propriété récent (< 3 mois) attestant de l'absence d'hypothèque ou de litige
3. Plan de bornage contradictoire certifié par un Géomètre Expert (OGEC).

Souhaitez-vous recevoir les copies numériques par WhatsApp ou par e-mail ?

À votre entière disposition pour tout éclaircissement cadastral,
{ownerName} — Cameroon Land Market`,
      },
      {
        id: 'offer_escrow_referral',
        title: 'Acceptation d’Offre & Compte Séquestre Notarié',
        category: 'negotiation',
        description: 'Validation du prix et consignation sur le compte séquestre du Notaire assermenté.',
        subject: 'Acceptation de votre offre pour {propertyTitle} — Procédure Notariée',
        template: `Bonjour {buyerName},

Nous avons le plaisir de vous confirmer que votre proposition d'acquisition de {propertyPrice} pour « {propertyTitle} » a été retenue favorablement.

Conformément à la réglementation foncière au Cameroun, la vente sera conclue par acte notarié authentique auprès de Maître {notaryName} (Notaire assermenté).

Afin de sécuriser la transaction et lancer la réquisition cadastrale MINDCAF, l'acompte de réservation ({escrowPercentage}%) devra être consigné directement sur le Compte Séquestre Notarié de l'étude (aucun versement d'espèces direct).

Convenons d'un créneau pour préparer la signature de la promesse de vente chez le Notaire.
{ownerName}`,
      },
      {
        id: 'counter_offer',
        title: 'Contre-Proposition & Négociation de Prix',
        category: 'negotiation',
        description: 'Contre-offre structurée avec détail des frais de mutation cadastrale.',
        subject: 'Contre-proposition pour « {propertyTitle} » ({propertyLocation})',
        template: `Bonjour {buyerName},

Nous vous remercions pour l'intérêt sérieux porté à notre bien « {propertyTitle} » à {propertyLocation}.

Après consultation des propriétaires, nous vous formulons une contre-proposition ferme à {counterPrice} (net vendeur).

Rappel des frais légaux au Cameroun à la charge de l'acquéreur :
• Droits d'enregistrement fiscaux (environ 5%)
• Honoraires et débours notariés réglementés (2 à 3%)
• Frais d'inscription au Livre Foncier MINDCAF.

Cette contre-proposition reste valable pendant 7 jours ouvrés.
Dans l'attente de votre retour,
{ownerName} (Tél : {ownerPhone})`,
      },
      {
        id: 'availability_brief',
        title: 'Confirmation de Disponibilité & Atouts',
        category: 'general',
        description: 'Accusé de réception confirmant l’état du bien et les raccordements ENEO/CAMWATER.',
        subject: 'Disponibilité confirmée : {propertyTitle} ({propertyLocation})',
        template: `Bonjour {buyerName},

Nous confirmons que le bien « {propertyTitle} » situé à {propertyLocation} proposé au prix de {propertyPrice} est disponible immédiatement.

Points forts vérifiés :
✓ Titre Foncier valide et vérifiable au Cadastre
✓ Façade sur voie carrossable praticable en toute saison
✓ Raccordements réseaux ENEO et CAMWATER à proximité immédiate.

Quand seriez-vous disponible pour un échange téléphonique ou pour convenir d'une visite sur place ?

Chaleureuses salutations,
{ownerName} (Tél : {ownerPhone})`,
      },
    ];
  }

  // English
  return [
    {
      id: 'visit_confirm',
      title: 'Site Visit Confirmation',
      category: 'visit',
      description: 'On-site appointment, meeting landmark, and guide contact details.',
      subject: 'Site Visit Confirmation: {propertyTitle} in {propertyLocation}',
      template: `Hello {buyerName},

We are pleased to confirm your scheduled site inspection for "{propertyTitle}" located at {propertyLocation} on {visitDate} at {visitTime}.

📍 Meeting Landmark: {meetingLandmark}
👤 Your on-site guide: {ownerName} (Phone: {ownerPhone})

💡 Helpful tip: For raw land parcels, comfortable boots or sneakers are recommended to easily walk the perimeter and inspect the official OGEC boundary beacons.

Please feel free to text or call when approaching the meeting point.
Best regards,
{ownerName} — Cameroon Land Market`,
    },
    {
      id: 'title_dossier_req',
      title: 'Land Title (MINDCAF) Dossier Requisition',
      category: 'legal',
      description: 'Sending official land certificate copy, ownership audit, and boundary surveyor plan.',
      subject: 'Land Title Dossier & Cadastral Verification: {propertyTitle}',
      template: `Hello {buyerName},

Following your inquiry on Cameroon Land Market regarding "{propertyTitle}" ({propertyLocation}), we are preparing your official cadastral compliance dossier in accordance with Cameroon Land Ordinance 74-1.

The legal dossier contains:
1. Certified true copy of the Land Certificate (Titre Foncier - MINDCAF)
2. Recent Certificate of Ownership (< 3 months) certifying zero mortgages, liens, or legal encumbrances
3. Official Cadastral Boundary Plan stamped by a Licensed Land Surveyor (OGEC).

Would you prefer to receive digital copies via WhatsApp or Email?

At your full disposal for any cadastral inquiries,
{ownerName} — Cameroon Land Market`,
    },
    {
      id: 'offer_escrow_referral',
      title: 'Offer Acceptance & Notarial Escrow Referral',
      category: 'negotiation',
      description: 'Formal acceptance of buyer offer and instructions for the compulsory notarial escrow account.',
      subject: 'Offer Accepted for {propertyTitle} — Notarial Escrow Procedure',
      template: `Hello {buyerName},

We are delighted to confirm that your purchase offer of {propertyPrice} for "{propertyTitle}" has been favorably accepted.

In compliance with Cameroon property conveyance laws, this transaction will be executed via authentic notarial deed before Maître {notaryName} (Certified Notary Public).

To formally secure the listing and initiate cadastral transfer requisitions, an earnest deposit of {escrowPercentage}% must be deposited into the official Notarial Escrow Account (Compte Séquestre Notarié) of the notary's chambers (no direct cash handover).

Let us know your availability this week to schedule the preliminary sale agreement at the notary's office.
{ownerName}`,
    },
    {
      id: 'counter_offer',
      title: 'Counter-Offer & Price Negotiation',
      category: 'negotiation',
      description: 'Structured counter-bid with details of transfer taxes and registration fees.',
      subject: 'Counter-Offer for "{propertyTitle}" ({propertyLocation})',
      template: `Hello {buyerName},

Thank you for your sincere interest in "{propertyTitle}" located at {propertyLocation}.

Following consultation with the legal title owners, we are pleased to extend a firm counter-offer at {counterPrice} (net seller).

Reminder of standard legal conveyance fees in Cameroon payable by the buyer:
• Fiscal registration stamp duties (approx. 5%)
• Regulated notary fees and disbursements (2 to 3%)
• MINDCAF Cadastral Registry entry fees.

This counter-offer is valid for 7 business days.
Looking forward to your response,
{ownerName} (Phone: {ownerPhone})`,
    },
    {
      id: 'availability_brief',
      title: 'Property Availability & Highlights Brief',
      category: 'general',
      description: 'Verification of property availability, road frontage, and utility access.',
      subject: 'Availability Confirmed: {propertyTitle} ({propertyLocation})',
      template: `Hello {buyerName},

We confirm that "{propertyTitle}" in {propertyLocation} listed at {propertyPrice} is actively available for transaction.

Verified Highlights:
✓ Clean Land Title (Titre Foncier) verifiable at the Cadastre
✓ All-season motorable road frontage
✓ ENEO electricity grid and CAMWATER utility lines nearby.

When would be a convenient time for a brief telephone call or to schedule an on-site visit?

Kind regards,
{ownerName} (Phone: {ownerPhone})`,
    },
  ];
}

/**
 * Replace placeholders like {buyerName}, {propertyTitle}, etc. in template text.
 */
export function fillTemplate(templateText: string, data: LeadPlaceholderData): string {
  let result = templateText;

  const defaults: Record<string, string> = {
    buyerName: data.buyerName || 'Cher Client / Dear Buyer',
    propertyTitle: data.propertyTitle || 'Bien Immobilier / Real Estate Property',
    propertyLocation: data.propertyLocation || 'Cameroun',
    propertyPrice: data.propertyPrice || 'Prix négociable / Price on request',
    ownerName: data.ownerName || 'Cameroon Land Market Team',
    ownerPhone: data.ownerPhone || '+237 690 00 00 00',
    visitDate: data.visitDate || 'Samedi prochain / Next Saturday',
    visitTime: data.visitTime || '10:00',
    meetingLandmark: data.meetingLandmark || 'Carrefour principal / Central Landmark',
    notaryName: data.notaryName || 'le Notaire instrumentaire / Appointed Notary',
    escrowPercentage: data.escrowPercentage || '10',
    counterPrice: data.counterPrice || data.propertyPrice || 'Prix convenu / Agreed price',
  };

  for (const [key, val] of Object.entries(defaults)) {
    const regex = new RegExp(`\\{${key}\\}`, 'g');
    result = result.replace(regex, val);
  }

  return result;
}

/**
 * Robustly sanitizes and formats a phone number for WhatsApp wa.me URLs.
 * Handles Cameroon local (9-digit), leading zeroes (06xx), country-code prefixed (237xx, +237xx),
 * and international Diaspora numbers (+33, +1, +44, etc.) without corruption.
 */
export function cleanWhatsAppPhone(rawPhone: string): string {
  if (!rawPhone) return '';
  // Defend against function call injections (e.g., alert(1)) and protocol injections
  const clean = rawPhone
    .replace(/[a-zA-Z_]+\s*\([^)]*\)/gi, '')
    .replace(/(javascript|data|vbscript):/gi, '')
    .trim();
  const hasPlus = clean.startsWith('+');
  let digits = clean.replace(/[^0-9]/g, '');
  if (!digits) return '';

  if (digits.startsWith('00')) {
    digits = digits.slice(2);
  }

  if (hasPlus) {
    return digits;
  }
  if (digits.startsWith('237')) {
    return digits;
  }
  if (digits.length === 9) {
    return `237${digits}`;
  }
  if (digits.length === 10 && digits.startsWith('0')) {
    return `237${digits.slice(1)}`;
  }
  if (digits.length <= 8) {
    return `237${digits}`;
  }
  return digits;
}

/**
 * Enterprise Strict Zero-Click WhatsApp Link Sanitizer:
 * Validates destination phone number and sanitizes outbound message text,
 * stripping null bytes, script protocols, and ensuring strict RFC compliance.
 */
export function generateWhatsAppUrl(rawPhone: string, message: string = ''): string {
  const waDigits = cleanWhatsAppPhone(rawPhone);
  // Phone must be between 8 and 16 digits
  if (!waDigits || waDigits.length < 8 || waDigits.length > 16) return '';

  if (!message) {
    return `https://wa.me/${waDigits}`;
  }

  // Strip control characters, null bytes, and script protocols from message
  const sanitizedMsg = message
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
    .replace(/(javascript|data|vbscript):/gi, '');

  return `https://wa.me/${waDigits}?text=${encodeURIComponent(sanitizedMsg)}`;
}

export const sanitizeWhatsAppUrl = generateWhatsAppUrl;

/**
 * Generate an RFC-compliant mailto: URL.
 */
export function generateMailtoUrl(toEmail: string, subject: string, body: string): string {
  if (!toEmail) return '';
  return `mailto:${encodeURIComponent(toEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

// ========================================================================
// Category-Specific Buyer WhatsApp Inquiry System
// High-conversion tailored templates for Land, Commercial, and Residential
// ========================================================================

export interface BuyerWhatsAppIntent {
  id: string;
  labelEn: string;
  labelFr: string;
  badgeEn: string;
  badgeFr: string;
  shortDescEn: string;
  shortDescFr: string;
  iconType: 'visit' | 'legal' | 'power' | 'offer';
}

export function getBuyerInquiryIntents(
  propertyType?: string,
  transactionType?: string
): BuyerWhatsAppIntent[] {
  const isRent = transactionType === 'rent';
  const type = propertyType?.toLowerCase() || 'land';

  if (type === 'land') {
    return [
      {
        id: 'standard',
        labelEn: 'Visit & Beacons',
        labelFr: 'Visite & Bornage',
        badgeEn: 'Site Walkthrough',
        badgeFr: 'Visite Terrain',
        shortDescEn: 'Request parcel visit & inspect surveyor beacons',
        shortDescFr: 'Planifier visite sur site et vérifier les bornes OGEC',
        iconType: 'visit',
      },
      {
        id: 'titre_cadastre',
        labelEn: 'Titre Foncier Check',
        labelFr: 'Dossier Titre Foncier',
        badgeEn: 'MINDCAF Legal',
        badgeFr: 'Légal MINDCAF',
        shortDescEn: 'Request Land Certificate number, volume & boundary plan',
        shortDescFr: 'Demander numéro Titre Foncier, volume et plan cadastral',
        iconType: 'legal',
      },
      {
        id: 'offer',
        labelEn: 'Price Negotiation',
        labelFr: 'Négociation Prix',
        badgeEn: 'Cash Offer',
        badgeFr: 'Offre Comptant',
        shortDescEn: 'Inquire about net-seller price per m²',
        shortDescFr: 'Discuter du prix net vendeur au m²',
        iconType: 'offer',
      },
    ];
  }

  if (type === 'commercial') {
    if (isRent) {
      return [
        {
          id: 'standard',
          labelEn: 'Lease Terms & Visit',
          labelFr: 'Bail & Visite',
          badgeEn: 'Viewing',
          badgeFr: 'Visite',
          shortDescEn: 'Deposit, advance months & viewing schedule',
          shortDescFr: 'Caution, mois d’avance et date de visite',
          iconType: 'visit',
        },
        {
          id: 'technical_terms',
          labelEn: '3-Phase Power & Frontage',
          labelFr: 'Triphasé & Façade',
          badgeEn: 'Specs',
          badgeFr: 'Technique',
          shortDescEn: 'Check 3-phase ENEO, water & customer foot traffic',
          shortDescFr: 'Compteur Triphasé ENEO, eau et flux piéton/façade',
          iconType: 'power',
        },
        {
          id: 'offer',
          labelEn: 'Rental Offer',
          labelFr: 'Proposition de Loyer',
          badgeEn: 'Negotiation',
          badgeFr: 'Négociation',
          shortDescEn: 'Discuss commercial lease duration & monthly rate',
          shortDescFr: 'Négocier loyer et durée du bail commercial',
          iconType: 'offer',
        },
      ];
    } else {
      return [
        {
          id: 'standard',
          labelEn: 'Premises Inspection',
          labelFr: 'Visite du Local',
          badgeEn: 'Inspection',
          badgeFr: 'Visite des Lieux',
          shortDescEn: 'Visit commercial property & inspect setup',
          shortDescFr: 'Visiter le local ou l’immeuble commercial',
          iconType: 'visit',
        },
        {
          id: 'titre_cadastre',
          labelEn: 'Title & Zoning',
          labelFr: 'Titre & Affectation',
          badgeEn: 'Commercial Title',
          badgeFr: 'Titre Commercial',
          shortDescEn: 'Commercial ERP zoning & Title Deed verification',
          shortDescFr: 'Zoning commercial, conformité ERP et Titre Foncier',
          iconType: 'legal',
        },
        {
          id: 'offer',
          labelEn: 'Acquisition Offer',
          labelFr: 'Offre d’Achat',
          badgeEn: 'Buyout',
          badgeFr: 'Offre Ferme',
          shortDescEn: 'Discuss purchase terms and rental yield',
          shortDescFr: 'Discuter du prix d’acquisition et rendement locatif',
          iconType: 'offer',
        },
      ];
    }
  }

  if (type === 'house' || type === 'apartment' || type === 'studio') {
    if (isRent) {
      return [
        {
          id: 'standard',
          labelEn: 'Move-in & Viewing',
          labelFr: 'Conditions & Visite',
          badgeEn: 'Viewing',
          badgeFr: 'Visite',
          shortDescEn: 'Deposit, advance rent & immediate availability',
          shortDescFr: 'Caution, mois d’avance et disponibilité',
          iconType: 'visit',
        },
        {
          id: 'technical_terms',
          labelEn: 'Water, ENEO & Security',
          labelFr: 'Eau, ENEO & Sécurité',
          badgeEn: 'Utilities',
          badgeFr: 'Commodités',
          shortDescEn: 'Check CAMWATER/borehole, prepaid meter & security',
          shortDescFr: 'Alimentation eau, compteur individuel et sécurité',
          iconType: 'power',
        },
        {
          id: 'offer',
          labelEn: 'Rental Proposal',
          labelFr: 'Offre de Loyer',
          badgeEn: 'Negotiation',
          badgeFr: 'Négociation',
          shortDescEn: 'Propose monthly rent for long-term lease',
          shortDescFr: 'Négocier loyer pour bail résidentiel',
          iconType: 'offer',
        },
      ];
    } else {
      return [
        {
          id: 'standard',
          labelEn: 'Home Walkthrough',
          labelFr: 'Visite de la Villa',
          badgeEn: 'Walkthrough',
          badgeFr: 'Visite',
          shortDescEn: 'Schedule an on-site property walkthrough',
          shortDescFr: 'Planifier une visite complète de la propriété',
          iconType: 'visit',
        },
        {
          id: 'titre_cadastre',
          labelEn: 'Title & Building Permit',
          labelFr: 'Titre & Permis de Bâtir',
          badgeEn: 'Legal Audit',
          badgeFr: 'Audit Légal',
          shortDescEn: 'Land title certificate and building compliance',
          shortDescFr: 'Titre Foncier direct et conformité permis de bâtir',
          iconType: 'legal',
        },
        {
          id: 'offer',
          labelEn: 'Purchase Offer',
          labelFr: 'Offre d’Achat',
          badgeEn: 'Firm Offer',
          badgeFr: 'Offre Ferme',
          shortDescEn: 'Submit serious cash offer for acquisition',
          shortDescFr: 'Faire une offre d’achat ferme et sérieuse',
          iconType: 'offer',
        },
      ];
    }
  }

  // Fallback
  return [
    {
      id: 'standard',
      labelEn: 'Availability & Visit',
      labelFr: 'Disponibilité & Visite',
      badgeEn: 'Standard',
      badgeFr: 'Standard',
      shortDescEn: 'Inquire about availability and schedule visit',
      shortDescFr: 'Confirmer disponibilité et organiser une visite',
      iconType: 'visit',
    },
    {
      id: 'offer',
      labelEn: 'Price Negotiation',
      labelFr: 'Discussion Prix',
      badgeEn: 'Negotiation',
      badgeFr: 'Négociation',
      shortDescEn: 'Discuss terms with the owner or manager',
      shortDescFr: 'Échanger sur le prix et les conditions',
      iconType: 'offer',
    },
  ];
}

export interface BuyerWhatsAppMessageOptions {
  property: Property;
  language: 'en' | 'fr';
  intentId?: string;
  propertyUrl?: string;
  customOfferPrice?: string;
}

export function buildBuyerWhatsAppMessage(options: BuyerWhatsAppMessageOptions): string {
  const { property, language, intentId = 'standard', propertyUrl, customOfferPrice } = options;
  const isFr = language === 'fr';
  const type = property.type?.toLowerCase() || 'land';
  const isRent = property.transactionType === 'rent';

  const cleanTitle = (isFr ? (property.title_fr || property.localizedTitle?.fr) : (property.title_en || property.localizedTitle?.en)) || property.title || (isFr ? 'Bien Immobilier' : 'Property Listing');
  const cleanLocation = property.location || (isFr ? 'Cameroun' : 'Cameroon');
  const cleanPrice = property.price || (isFr ? 'Prix sur demande' : 'Price on request');
  const fullUrl = propertyUrl
    ? (propertyUrl.startsWith('http') ? propertyUrl : `https://cameroonlandmarket.com${propertyUrl}`)
    : `https://cameroonlandmarket.com`;

  // Title deed text
  const titleDeedText = property.titreFoncier
    ? (isFr ? `Titre Foncier N° ${property.titreFoncier}` : `Land Certificate N° ${property.titreFoncier}`)
    : (property.verificationStatus === 'verified'
      ? (isFr ? 'Titre Vérifié au Cadastre' : 'Verified Land Title')
      : (isFr ? 'Non encore vérifié' : 'Not Yet Verified'));

  // 1. LAND
  if (type === 'land') {
    if (intentId === 'titre_cadastre') {
      return isFr
        ? `👋 Bonjour,\n\nJe vous contacte au sujet de votre terrain sur Cameroon Land Market :\n🏡 *${cleanTitle}*\n📍 *Localisation :* ${cleanLocation}\n💰 *Prix affiché :* ${cleanPrice}\n📜 *Statut Foncier :* ${titleDeedText}\n🔗 *Lien :* ${fullUrl}\n\nJe suis vivement intéressé(e). Pourriez-vous me transmettre les précisions cadastrales suivantes :\n1. Numéro et Volume du Titre Foncier (Livre Foncier MINDCAF)\n2. Confirmation du plan de bornage contradictoire (bornes OGEC intactes)\n3. Certificat de propriété récent disponible ?\n\nMerci d'avance pour vos précisions.`
        : `👋 Hello,\n\nI am contacting you regarding your land parcel on Cameroon Land Market:\n🏡 *${cleanTitle}*\n📍 *Location:* ${cleanLocation}\n💰 *Asking Price:* ${cleanPrice}\n📜 *Title Status:* ${titleDeedText}\n🔗 *Link:* ${fullUrl}\n\nI am very interested in this parcel. Could you kindly share the following cadastral details:\n1. Land Certificate (Titre Foncier) Number & MINDCAF Registry Volume\n2. Confirmation of official boundary beacon survey (OGEC beacons intact)\n3. Is a recent Certificate of Ownership available?\n\nThank you for your assistance.`;
    }

    if (intentId === 'offer') {
      const offerClause = customOfferPrice
        ? (isFr ? `\n💡 Je souhaite vous soumettre une offre à : *${customOfferPrice}*.` : `\n💡 I would like to submit a cash offer of: *${customOfferPrice}*.`)
        : '';

      return isFr
        ? `👋 Bonjour,\n\nJe vous contacte au sujet de votre terrain sur Cameroon Land Market :\n🏡 *${cleanTitle}*\n📍 *Localisation :* ${cleanLocation}\n💰 *Prix affiché :* ${cleanPrice}\n📜 *Statut Foncier :* ${titleDeedText}\n🔗 *Lien :* ${fullUrl}\n\nCe terrain correspond parfaitement à mon projet.${offerClause}\nSeriez-vous ouvert(e) à une discussion sur le prix net vendeur pour un acquéreur sérieux et solvable ?\nQuel est votre meilleur prix au mètre carré (m²) ? Merci.`
        : `👋 Hello,\n\nI am contacting you regarding your land parcel on Cameroon Land Market:\n🏡 *${cleanTitle}*\n📍 *Location:* ${cleanLocation}\n💰 *Asking Price:* ${cleanPrice}\n📜 *Title Status:* ${titleDeedText}\n🔗 *Link:* ${fullUrl}\n\nThis parcel matches my investment criteria.${offerClause}\nWould you be open to discussing the net-seller price for a serious, ready buyer?\nWhat is your best cash rate per square meter (m²)? Thank you.`;
    }

    // Default: Site Visit & Beacons
    return isFr
      ? `👋 Bonjour,\n\nJe vous contacte au sujet de votre terrain sur Cameroon Land Market :\n🏡 *${cleanTitle}*\n📍 *Localisation :* ${cleanLocation}\n💰 *Prix affiché :* ${cleanPrice}\n📜 *Statut Foncier :* ${titleDeedText}\n🔗 *Lien :* ${fullUrl}\n\nLe terrain est-il toujours disponible ?\nJ'aimerais organiser une visite sur place pour inspecter la parcelle et le bornage cadastral. Quel créneau vous conviendrait ? Merci.`
      : `👋 Hello,\n\nI am contacting you regarding your land parcel on Cameroon Land Market:\n🏡 *${cleanTitle}*\n📍 *Location:* ${cleanLocation}\n💰 *Asking Price:* ${cleanPrice}\n📜 *Title Status:* ${titleDeedText}\n🔗 *Link:* ${fullUrl}\n\nIs this land parcel still available?\nI would like to schedule an on-site visit to inspect the perimeter beacons and boundary lines. What date/time works best for you? Thank you.`;
  }

  // 2. COMMERCIAL
  if (type === 'commercial') {
    if (isRent) {
      if (intentId === 'technical_terms') {
        return isFr
          ? `👋 Bonjour,\n\nJe vous contacte pour votre local commercial en location sur Cameroon Land Market :\n🏬 *${cleanTitle}*\n📍 *Localisation :* ${cleanLocation}\n💰 *Loyer affiché :* ${cleanPrice}\n🔗 *Lien :* ${fullUrl}\n\nPour valider la faisabilité technique de mon activité, pourriez-vous me préciser :\n1. Électricité : Compteur Triphasé ENEO dédié disponible ?\n2. Eau : Raccordement CAMWATER continu ou forage/réserve ?\n3. Visibilité : Linéaire de vitrine / façade et accessibilité clientèle ?\n\nMerci d'avance pour vos précisions.`
          : `👋 Hello,\n\nI am contacting you regarding your commercial space for rent on Cameroon Land Market:\n🏬 *${cleanTitle}*\n📍 *Location:* ${cleanLocation}\n💰 *Asking Rent:* ${cleanPrice}\n🔗 *Link:* ${fullUrl}\n\nTo assess suitability for my commercial operations, could you please clarify:\n1. Power: Dedicated 3-phase (triphasé) ENEO meter installed?\n2. Water: Regular CAMWATER connection or borehole/reserve tank?\n3. Visibility: Frontage window display & customer parking accessibility?\n\nThank you for the information.`;
      }

      if (intentId === 'offer') {
        const offerClause = customOfferPrice
          ? (isFr ? `\n💡 Je vous propose un loyer mensuel de : *${customOfferPrice}*.` : `\n💡 I would like to propose a monthly rent of: *${customOfferPrice}*.`)
          : '';

        return isFr
          ? `👋 Bonjour,\n\nJe vous contacte pour votre local commercial en location sur Cameroon Land Market :\n🏬 *${cleanTitle}*\n📍 *Localisation :* ${cleanLocation}\n💰 *Loyer affiché :* ${cleanPrice}\n🔗 *Lien :* ${fullUrl}\n\nLe local correspond à mes besoins d'implantation.${offerClause}\nSeriez-vous disposé(e) à échanger sur les conditions du bail commercial pour une occupation longue durée ? Merci.`
          : `👋 Hello,\n\nI am contacting you regarding your commercial space for rent on Cameroon Land Market:\n🏬 *${cleanTitle}*\n📍 *Location:* ${cleanLocation}\n💰 *Asking Rent:* ${cleanPrice}\n🔗 *Link:* ${fullUrl}\n\nThis commercial space fits my business requirements.${offerClause}\nWould you be open to discussing the lease conditions for a long-term commercial lease? Thank you.`;
      }

      // Default: Lease terms & visit
      return isFr
        ? `👋 Bonjour,\n\nJe vous contacte pour votre local commercial en location sur Cameroon Land Market :\n🏬 *${cleanTitle}*\n📍 *Localisation :* ${cleanLocation}\n💰 *Loyer affiché :* ${cleanPrice}\n🔗 *Lien :* ${fullUrl}\n\nLe local est-il disponible ? Quelles sont les modalités d'entrée (nombre de mois de caution et de loyers d'avance) ?\nJe souhaiterais convenir d'une visite des lieux. Merci.`
        : `👋 Hello,\n\nI am contacting you regarding your commercial space for rent on Cameroon Land Market:\n🏬 *${cleanTitle}*\n📍 *Location:* ${cleanLocation}\n💰 *Asking Rent:* ${cleanPrice}\n🔗 *Link:* ${fullUrl}\n\nIs this space currently available? What are the lease requirements (security deposit and advance months)?\nI would like to schedule a viewing. Thank you.`;
    } else {
      // Commercial Sale
      if (intentId === 'titre_cadastre') {
        return isFr
          ? `👋 Bonjour,\n\nAu sujet de votre bien commercial en vente sur Cameroon Land Market :\n🏢 *${cleanTitle}*\n📍 *Localisation :* ${cleanLocation}\n💰 *Prix :* ${cleanPrice}\n📜 *Statut Foncier :* ${titleDeedText}\n🔗 *Lien :* ${fullUrl}\n\nPourriez-vous me préciser les documents juridiques :\n1. Numéro du Titre Foncier individuel\n2. Affectation urbanistique / zoning commercial approuvé\n3. État locatif actuel (libre ou occupé avec baux en cours) ?\n\nMerci d'avance.`
          : `👋 Hello,\n\nRegarding your commercial property for sale on Cameroon Land Market:\n🏢 *${cleanTitle}*\n📍 *Location:* ${cleanLocation}\n💰 *Price:* ${cleanPrice}\n📜 *Title Status:* ${titleDeedText}\n🔗 *Link:* ${fullUrl}\n\nCould you please share legal compliance details:\n1. Individual Land Certificate (Titre Foncier) number\n2. Approved municipal commercial zoning\n3. Tenancy status (vacant or tenanted with ongoing leases)?\n\nThank you.`;
      }

      return isFr
        ? `👋 Bonjour,\n\nJe vous contacte pour votre bien commercial en vente sur Cameroon Land Market :\n🏢 *${cleanTitle}*\n📍 *Localisation :* ${cleanLocation}\n💰 *Prix :* ${cleanPrice}\n📜 *Statut Foncier :* ${titleDeedText}\n🔗 *Lien :* ${fullUrl}\n\nLe bien est-il toujours disponible pour une acquisition ?\nJe souhaiterais planifier une visite sur place pour examiner l'immeuble. Merci.`
        : `👋 Hello,\n\nI am contacting you regarding your commercial property for sale on Cameroon Land Market:\n🏢 *${cleanTitle}*\n📍 *Location:* ${cleanLocation}\n💰 *Price:* ${cleanPrice}\n📜 *Title Status:* ${titleDeedText}\n🔗 *Link:* ${fullUrl}\n\nIs this commercial property actively available for acquisition?\nI would like to arrange an on-site visit to inspect the building and premises. Thank you.`;
    }
  }

  // 3. RESIDENTIAL (House / Apartment / Studio)
  if (type === 'house' || type === 'apartment' || type === 'studio') {
    if (isRent) {
      if (intentId === 'technical_terms') {
        return isFr
          ? `👋 Bonjour,\n\nConcernant votre logement en location sur Cameroon Land Market :\n🏠 *${cleanTitle}*\n📍 *Localisation :* ${cleanLocation}\n💰 *Loyer :* ${cleanPrice}\n🔗 *Lien :* ${fullUrl}\n\nPourriez-vous me renseigner sur les commodités :\n1. Eau : CAMWATER régulier, cuve de réserve ou forage ?\n2. Électricité : Compteur ENEO prépayé individuel ?\n3. Sécurité : Clôture fermée, gardiennage / portail sécurisé ?\n4. Accessibilité : Voie praticable en saison des pluies ?\n\nMerci d'avance pour vos réponses.`
          : `👋 Hello,\n\nRegarding your residential rental on Cameroon Land Market:\n🏠 *${cleanTitle}*\n📍 *Location:* ${cleanLocation}\n💰 *Rent:* ${cleanPrice}\n🔗 *Link:* ${fullUrl}\n\nCould you please share details about the utilities and security:\n1. Water: Regular CAMWATER supply, storage tank, or borehole?\n2. Electricity: Dedicated individual prepaid ENEO meter?\n3. Security: Perimeter wall, security guard / gated compound?\n4. Access road: Motorable road condition during rainy season?\n\nThank you for the guidance.`;
      }

      if (intentId === 'offer') {
        const offerClause = customOfferPrice
          ? (isFr ? `\n💡 Je vous propose un loyer de : *${customOfferPrice}*.` : `\n💡 I would like to propose a rent of: *${customOfferPrice}*.`)
          : '';

        return isFr
          ? `👋 Bonjour,\n\nConcernant votre logement en location sur Cameroon Land Market :\n🏠 *${cleanTitle}*\n📍 *Localisation :* ${cleanLocation}\n💰 *Loyer :* ${cleanPrice}\n🔗 *Lien :* ${fullUrl}\n\nLe logement correspond à ce que je recherche.${offerClause}\nSeriez-vous ouvert(e) à une négociation du loyer pour un contrat de bail sérieux et stable ? Merci.`
          : `👋 Hello,\n\nRegarding your residential rental on Cameroon Land Market:\n🏠 *${cleanTitle}*\n📍 *Location:* ${cleanLocation}\n💰 *Rent:* ${cleanPrice}\n🔗 *Link:* ${fullUrl}\n\nThis home matches what I am looking for.${offerClause}\nWould you be open to discussing the monthly rent for a reliable, long-term tenancy? Thank you.`;
      }

      // Default: Viewing & terms
      return isFr
        ? `👋 Bonjour,\n\nJe vous contacte au sujet de votre logement en location sur Cameroon Land Market :\n🏠 *${cleanTitle}*\n📍 *Localisation :* ${cleanLocation}\n💰 *Loyer :* ${cleanPrice}\n🔗 *Lien :* ${fullUrl}\n\nLe logement est-il disponible pour une entrée rapide ? Quelles sont les conditions d'entrée (caution et mois d'avance) ?\nQuand pouvons-nous organiser une visite ? Merci.`
        : `👋 Hello,\n\nI am contacting you regarding your residential rental on Cameroon Land Market:\n🏠 *${cleanTitle}*\n📍 *Location:* ${cleanLocation}\n💰 *Rent:* ${cleanPrice}\n🔗 *Link:* ${fullUrl}\n\nIs this home available for immediate move-in? What are the deposit and advance payment terms?\nWhen can we schedule a viewing? Thank you.`;
    } else {
      // Residential Sale
      if (intentId === 'titre_cadastre') {
        return isFr
          ? `👋 Bonjour,\n\nConcernant votre maison / villa en vente sur Cameroon Land Market :\n🏡 *${cleanTitle}*\n📍 *Localisation :* ${cleanLocation}\n💰 *Prix :* ${cleanPrice}\n📜 *Statut Foncier :* ${titleDeedText}\n🔗 *Lien :* ${fullUrl}\n\nPourriez-vous me transmettre les détails juridiques :\n1. La maison est-elle assise sur un Titre Foncier direct et individuel ?\n2. Un permis de bâtir / plan de conformité est-il disponible ?\n3. Le certificat de propriété récent est-il prêt ?\n\nMerci d'avance pour votre retour.`
          : `👋 Hello,\n\nRegarding your house / villa for sale on Cameroon Land Market:\n🏡 *${cleanTitle}*\n📍 *Location:* ${cleanLocation}\n💰 *Price:* ${cleanPrice}\n📜 *Title Status:* ${titleDeedText}\n🔗 *Link:* ${fullUrl}\n\nCould you please clarify the legal documentation:\n1. Is the home seated on an individual Land Title (Titre Foncier)?\n2. Is an approved building permit / conformity plan available?\n3. Is a recent Certificate of Ownership ready for inspection?\n\nThank you for your response.`;
      }

      return isFr
        ? `👋 Bonjour,\n\nJe vous contacte au sujet de votre maison / villa en vente sur Cameroon Land Market :\n🏡 *${cleanTitle}*\n📍 *Localisation :* ${cleanLocation}\n💰 *Prix :* ${cleanPrice}\n📜 *Statut Foncier :* ${titleDeedText}\n🔗 *Lien :* ${fullUrl}\n\nLe bien est-il toujours disponible ?\nJ'aimerais planifier une visite sur place pour inspecter la villa et les abords. Merci.`
        : `👋 Hello,\n\nI am contacting you regarding your house / villa for sale on Cameroon Land Market:\n🏡 *${cleanTitle}*\n📍 *Location:* ${cleanLocation}\n💰 *Price:* ${cleanPrice}\n📜 *Title Status:* ${titleDeedText}\n🔗 *Link:* ${fullUrl}\n\nIs this home still available for sale?\nI would like to schedule an on-site visit to inspect the property and surroundings. Thank you.`;
    }
  }

  // 4. Fallback for others (Hotels, etc.)
  return isFr
    ? `👋 Bonjour,\n\nJe vous contacte au sujet de votre annonce sur Cameroon Land Market :\n🏡 *${cleanTitle}*\n📍 *Localisation :* ${cleanLocation}\n💰 *Prix affiché :* ${cleanPrice}\n📜 *Statut Foncier :* ${titleDeedText}\n🔗 *Lien :* ${fullUrl}\n\nEst-elle toujours disponible pour organiser une visite ? Merci d'avance.`
    : `👋 Hello,\n\nI am contacting you regarding your listing on Cameroon Land Market:\n🏡 *${cleanTitle}*\n📍 *Location:* ${cleanLocation}\n💰 *Asking Price:* ${cleanPrice}\n📜 *Title Status:* ${titleDeedText}\n🔗 *Link:* ${fullUrl}\n\nIs this property still available for a visit? Thank you.`;
}

export function buildBuyerWhatsAppUrl(
  options: BuyerWhatsAppMessageOptions & { rawPhone: string }
): string {
  const { rawPhone, ...msgOptions } = options;
  const message = buildBuyerWhatsAppMessage(msgOptions);
  return generateWhatsAppUrl(rawPhone, message);
}

